import React, { useState } from "react";
import axios from "axios";
import { posApi, posErr } from "./posApi";
import { MapPin, User, DoorOpen } from "lucide-react";

const C = { bg: "#FAF7F0", surf: "#FFFFFF", surf2: "#FFFCF7", crimson: "#8B0000", crimsonD: "#6B0000", gold: "#B8860B", gold2: "#C9A84C", text: "#3B2722", muted: "#765A4A", border: "#E9DED0", error: "#A52222" };
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PinLogin({ onDone }) {
  const [step, setStep] = useState("pin");
  const [pin, setPin] = useState("");
  const [user, setUser] = useState(null);
  const [locName, setLocName] = useState("");
  const [tills, setTills] = useState([]);
  const [till, setTill] = useState("");
  const [float0, setFloat0] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const press = async (d) => { setErr(""); const next = (pin + d).slice(0, 4); setPin(next); if (next.length === 4) await submitPin(next); };
  const submitPin = async (p) => {
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${BASE}/pos/pin-login`, { pin: p });
      localStorage.setItem("pos_token", data.token);
      setUser(data.user); setLocName(data.location_name); setStep("till");
      try {
        const { data: rows } = await posApi.get("/tills", { params: { location_id: data.user.location_id } });
        const active = (rows || []).filter((t) => t.active !== false);
        setTills(active);
        if (active.length) setTill(active[0].name);
      } catch { setTills([]); }
    } catch (e) { setErr(posErr(e)); setPin(""); } finally { setBusy(false); }
  };
  const startShift = async () => {
    if (!till) { setErr("Select the till you are working from"); return; }
    setBusy(true);
    try { const { data } = await posApi.post("/pos/shift/open", { till, opening_float: Number(float0 || 0) }); onDone(user, { ...data, location_name: locName }); }
    catch (e) { setErr(posErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="pos-root" style={{ alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,#FAF7F0,#F3E9DA)" }} data-testid="pos-pin-screen">
      <div style={{ width: 380, background: C.surf, border: `1px solid ${C.border}`, borderRadius: 22, padding: 34, boxShadow: "0 30px 70px rgba(139,0,0,.12)" }}>
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <img src="/logo.jpg" alt="Sir Monty" data-testid="pos-login-logo" style={{ width: 200, maxWidth: "100%", height: "auto", objectFit: "contain", margin: "0 auto 6px", display: "block" }} />
          <div className="label" style={{ color: C.gold, fontSize: 10, marginTop: 2 }}>Point of Sale · Front Office</div>
          <div className="display" style={{ fontSize: 26, fontWeight: 600, marginTop: 14, color: C.text }}>
            {step === "pin" ? "Enter your PIN" : `Welcome, ${user?.name?.split(" ")[0]}`}
          </div>
          {step === "till" && <div style={{ color: C.muted, fontSize: 13, marginTop: 3 }}>{user?.role?.name} · {locName}</div>}
        </div>

        {step === "pin" ? (
          <>
            <div style={{ display: "flex", justifyContent: "center", gap: 14, margin: "6px 0 24px" }}>
              {[0, 1, 2, 3].map((i) => <div key={i} style={{ width: 15, height: 15, borderRadius: "50%", background: i < pin.length ? C.crimson : "transparent", border: `2px solid ${i < pin.length ? C.crimson : C.border}`, transition: "background .15s" }} />)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => <button key={n} className="pos-key" data-testid={`pin-key-${n}`} onClick={() => press(String(n))} disabled={busy}>{n}</button>)}
              <button className="pos-key" onClick={() => setPin("")} data-testid="pin-clear" style={{ color: C.muted, fontSize: 15 }}>CLR</button>
              <button className="pos-key" data-testid="pin-key-0" onClick={() => press("0")} disabled={busy}>0</button>
              <button className="pos-key" onClick={() => setPin(pin.slice(0, -1))} style={{ color: C.muted, fontSize: 22 }}>⌫</button>
            </div>
          </>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <div className="label" style={{ fontSize: 10, color: "#765A4A", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: "#B8860B" }}><MapPin size={15} /></span>Till / Register
              </div>
              <select value={till} onChange={(e) => setTill(e.target.value)} data-testid="till-name"
                style={{ width: "100%", background: "#FFFCF7", border: "1px solid #E9DED0", borderRadius: 10, padding: "12px 14px", color: "#3B2722", fontSize: 15 }}>
                {tills.length === 0 && <option value="">No till configured for this branch</option>}
                {tills.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.terminal_row ? `${t.name} · Terminal row ${t.terminal_row}` : t.name}
                  </option>
                ))}
              </select>
              {tills.length === 0 && <div style={{ color: C.error, fontSize: 11, marginTop: 6 }}>Ask an admin to configure a till for {locName} first.</div>}
            </div>
            <Field icon={<User size={15} />} label="Opening cash float (₦)" value={float0} onChange={setFloat0} type="number" placeholder="0" testid="opening-float" />
            <button onClick={startShift} disabled={busy} data-testid="start-shift-btn" className="label"
              style={{ background: C.crimson, color: "#fff", border: "none", borderRadius: 12, padding: "15px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              {busy ? "STARTING…" : "START SHIFT"}
            </button>
          </div>
        )}
        {err && <div data-testid="pin-error" style={{ color: C.error, textAlign: "center", marginTop: 16, fontSize: 13 }}>{err}</div>}
        <div style={{ textAlign: "center", marginTop: 20, color: C.muted, fontSize: 11 }}><DoorOpen size={12} style={{ verticalAlign: "-2px" }} /> Front-office cashier access only</div>
      </div>
    </div>
  );
}

function Field({ icon, label, value, onChange, type, placeholder, testid }) {
  return (
    <div>
      <div className="label" style={{ fontSize: 10, color: "#765A4A", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}><span style={{ color: "#B8860B" }}>{icon}</span>{label}</div>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        style={{ width: "100%", background: "#FFFCF7", border: "1px solid #E9DED0", borderRadius: 10, padding: "12px 14px", color: "#3B2722", fontSize: 15 }} />
    </div>
  );
}
